import {
  validateArrayOf,
  validateNotNullishObject,
  validatePrimitive,
} from "@faro-lotv/foundation";

/** Identifies the messages sent by the embedded IPX ordering surface */
export const IPX_EMBED_MESSAGE_SOURCE = "ipx-embed";

const CURRENCY_CODE = /^[A-Z]{3}$/;

/** Deliverables that can be ordered from IPX, floorPlan is drawn and reviewed by the IPX team */
export type IpxDeliverable = "aiFloorPlan" | "floorPlan" | "bimModel";

/** An order confirmed and paid in the IPX ordering surface */
export type IpxOrder = {
  /** IPX identifier of the order */
  orderId: string;

  /** Total price */
  price: number;

  /** ISO 4217 currency of the price */
  currency: string;

  /** What IPX will deliver */
  deliverables: IpxDeliverable[];

  /** Level of detail of the BIM model */
  levelOfDetail: string;

  /** Estimated delivery time in business days */
  deliveryDays: number;
};

/** Messages the IPX ordering surface posts to the viewer */
export type IpxEmbedMessage =
  | {
      source: typeof IPX_EMBED_MESSAGE_SOURCE;
      type: "orderConfirmed";
      order: IpxOrder;
    }
  | {
      source: typeof IPX_EMBED_MESSAGE_SOURCE;
      type: "orderCancelled";
    };

function isIpxDeliverable(value: unknown): value is IpxDeliverable {
  return (
    value === "aiFloorPlan" || value === "floorPlan" || value === "bimModel"
  );
}

function isIpxOrder(value: unknown): value is IpxOrder {
  return (
    validateNotNullishObject<IpxOrder>(value, "order") &&
    validatePrimitive(value, "orderId", "string") &&
    validatePrimitive(value, "price", "number") &&
    validatePrimitive(value, "currency", "string") &&
    CURRENCY_CODE.test(value.currency ?? "") &&
    validatePrimitive(value, "levelOfDetail", "string") &&
    validatePrimitive(value, "deliveryDays", "number") &&
    validateArrayOf({
      object: value,
      prop: "deliverables",
      elementGuard: isIpxDeliverable,
    }) &&
    !!value.deliverables?.length
  );
}

/**
 * @param data The payload of a message event
 * @returns true if the payload is a well formed message of the IPX ordering surface
 */
export function isIpxEmbedMessage(data: unknown): data is IpxEmbedMessage {
  if (
    !validateNotNullishObject<IpxEmbedMessage>(data, "message") ||
    data.source !== IPX_EMBED_MESSAGE_SOURCE
  ) {
    return false;
  }
  if (data.type === "orderCancelled") return true;
  return (
    data.type === "orderConfirmed" && "order" in data && isIpxOrder(data.order)
  );
}
